// src/utils/loanMath.js
//
// Pure derivations for loan records (PRD_LoansManagement C2 / C5 / C7 / C13).
// No Firestore, no Date arithmetic — month keys are 'YYYY-MM' strings and
// compare lexically. The balance is DERIVED from what approved runs actually
// took (paidTotal), never stored, so it cannot drift (C5).

import { round2 } from '../services/contributionTableClient';

// C4 — there is no `type` field. The loan NAME is the descriptive type ("SSS
// Salary Loan", "Cash Advance"). The enum was rendered as a muted subtitle in
// three places and copied onto every run line, but nothing filtered, grouped,
// summed or reported by it, so the name carries the same meaning with one fewer
// field to pick (founder, /test-impl 2026-09-24). Removed before any loan data
// existed. (An earlier version of this comment said it "only ever defaulted the
// name" — it also rendered; /impl-review r2.)

// Tolerant numeric read — Firestore numbers, form strings, missing → 0.
export const num = (v) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Remaining to deduct (C4/C5): total payable minus what was paid before the
 * loan was entered minus what approved runs have credited. Clamped at 0 so an
 * over-collection or a lowered total never shows a negative peso figure.
 */
export const loanRemaining = (loan) =>
  Math.max(0, round2(num(loan?.totalPayable) - num(loan?.paidBefore) - num(loan?.paidTotal)));

/** C7 — active while there is something left to deduct and it is neither closed nor voided. */
export const isActive = (loan) =>
  !loan?.metadata?.isDeleted && !loan?.closedAt && loanRemaining(loan) > 0;

/** C13 — the Approve write-back credits every loan that is not voided (paused and closed included). */
export const isCreditable = (loan) => !loan?.metadata?.isDeleted;

/**
 * C2 — what is left of this month's amortization, capped at the loan's
 * remaining balance so the last installment never overshoots.
 */
export const monthRemaining = (loan, monthKey) => {
  const target = num(loan?.monthlyAmortization);
  const deducted = num(loan?.paidByMonth?.[monthKey]);
  return Math.max(0, round2(Math.min(target - deducted, loanRemaining(loan))));
};

/**
 * C2 — the earliest period month the loan is payable in (>= firstDeductionMonth),
 * or null when the loan is not yet payable in any month of the period.
 * @param {Object} loan
 * @param {string[]} periodMonthKeys - 'YYYY-MM' keys of the period's months
 */
export const attributionMonth = (loan, periodMonthKeys) => {
  const first = loan?.firstDeductionMonth;
  if (typeof first !== 'string' || !first) return null;
  const keys = (periodMonthKeys || []).filter((k) => typeof k === 'string').sort();
  return keys.find((k) => k >= first) || null;
};

// C2 — there is NO pre-fill. `defaultFill` (half the monthly amortization) was
// removed at /test-impl 2026-09-26: it assumed exactly two cutoffs a month, and
// the wizard accepts custom ranges. `monthRemaining` survives as what the chip
// OFFERS; the input itself is capped at the loan's balance, not the month's.

/** C13 — appears in the Loans step: active, not paused, payable in some period month. */
export const qualifies = (loan, periodMonthKeys) =>
  isActive(loan) && !loan?.paused && attributionMonth(loan, periodMonthKeys) !== null;

export const LOAN_STATUS = {
  ACTIVE: 'active',
  PAUSED: 'paused',
  CLOSED: 'closed',
  VOIDED: 'voided',
};

/** C7 — the display status, derived. */
export const loanStatus = (loan) => {
  if (loan?.metadata?.isDeleted) return LOAN_STATUS.VOIDED;
  if (loan?.closedAt || loanRemaining(loan) <= 0) return LOAN_STATUS.CLOSED;
  if (loan?.paused) return LOAN_STATUS.PAUSED;
  return LOAN_STATUS.ACTIVE;
};

/**
 * C5/C12 — the payment history in stored (append) order with a running
 * "balance after" column. The running value is kept unclamped internally so a
 * later correction lands where it should; only the displayed figure floors at 0.
 * @returns {Array<Object>} each payment plus { balanceAfter }
 */
export const paymentsWithBalance = (loan) => {
  let running = round2(num(loan?.totalPayable) - num(loan?.paidBefore));
  return (Array.isArray(loan?.payments) ? loan.payments : []).map((p) => {
    running = round2(running - num(p?.amount));
    return { ...p, balanceAfter: Math.max(0, running) };
  });
};
